/**
 * The application chrome — an ink rail on the inline start, a 56px bar above the content.
 *
 * ADR 9004. This replaced a two-row header: chapters on row one, the active chapter's own
 * screens on row two. The redesign the client approved moves both rows into a vertical
 * rail, which is what makes the second level permanent — row two only existed while a
 * chapter was active, so the planner could not see a chapter's screens and the content at
 * the same time.
 *
 * **The navigation model did not change with the layout, and must not.** Everything below
 * still reads `chapterNavigation()` and `notOffered()` from `src/features/registry`, so a
 * chapter added to a manifest appears here with no file in this directory edited. The
 * behaviours `AppChrome.test.tsx` pins — a not-yet-offered chapter is a chrome fact rather
 * than a routing one, a deep link into one is a LOCATION rather than a preview, brand red
 * marks only where the planner actually is, nothing is dimmed with opacity — are the same
 * behaviours, asserted on the same roles and the same accessible names. A rail is a
 * different shape for the same statements about the estate.
 *
 * **Why the rail does not invert between themes.** `rail-*` is ink in light and a step
 * darker in dark, rather than flipping to a pale plane. It is a persistent edge of the
 * window, not a surface the content sits on, and a chrome that changed sides of the
 * contrast ratio with the theme would make every screenshot pair unreadable against each
 * other. `tokens/brand.json` carries the same note beside the values.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, LayoutGrid, ListChecks, LogOut, Moon, Sun, Monitor, Search } from 'lucide-react';
import { BataWordmark } from './BataWordmark';
import { AccountEntries } from './AccountEntries';
import { IdentityMark } from './RailGlyphs';
import { CollapsedRail } from './CollapsedRail';
import { useWideRail } from './useWideRail';
import {
  activeRailLink, firstUsable, isFutureHere, railGroupOf, railLayout, railNavigationParams, withoutRunParams, FUTURE_GROUP, type RailLink,
} from './rail-layout';
import { useWithheldPaths } from './rail-capabilities';
import { moveRailFocus, RAIL_ITEM, useRailOpenGroups } from './rail-groups';
import { RailApps } from './RailApps';
import { CommandPalette, isAppleKeyboard } from './CommandPalette';
import { TopBarSlotProvider } from './top-bar-slot';
import type { AppChromeProps } from './types';
import { HOME_PATH, resolveRoute } from '../routes/router';
import { declaredUrlParams, scopeChipLabelKey, scopeChips, scopeChipValueIsOpaqueId, scopeChipValueKey, scopeChipVocabulary } from './scope-chips';
import { IdTag } from '../../planning/run-words';
import { useReasonLabels } from '../../api/reason-labels';
import { launchReadReasonLabels } from '../../api/generated';
import { useVocabularyLabels, VOCABULARY_DEPARTMENT_FAMILY, VOCABULARY_SEASON } from '../../api/vocabulary-labels';
import { MY_WORK_PATH } from '../routes/my-work/my-work';
import { WorkBadge } from '../routes/my-work/WorkBadge';
import { SessionClaimsSync } from '../../auth/session-claims-sync';
import { FetchProgressBar } from './FetchProgressBar';

/**
 * The chip of the constraint that bound a launch line (`?f.reason=`): the platform's words for the code, «Constraint: Capped by capacity»,
 * with the code behind «id». The words are read only when the chip is drawn, once for the session; until they arrive (or where the platform
 * has none) the chip names the parameter and keeps the code behind «id», as any identifier.
 */
function ReasonChip({ label, code }: { readonly label: string; readonly code: string }): React.JSX.Element {
  const { t } = useTranslation();
  const words = useReasonLabels(launchReadReasonLabels).labelOrNull('lineReason', code);
  return (
    <span title={words === null ? label : t('nav.scopeChipLabelled', { label, value: words })} className="bg-pill text-caption text-ink-primary rounded-chip inline-flex max-w-[16rem] items-baseline gap-1 px-2 py-1">
      <span className="truncate">{words === null ? label : t('nav.scopeChipLabelled', { label, value: words })}</span>
      <IdTag id={code} kind={label} />
    </span>
  );
}

/** The shell's own places at the head of the rail: the Cockpit, then "My work" (ADR 9022). */
const SHELL_ENTRIES = [
  { path: HOME_PATH, labelKey: 'nav.home', icon: LayoutGrid },
  { path: MY_WORK_PATH, labelKey: 'nav.myWork', icon: ListChecks },
] as const;
import { chapterNavigation, screensOf } from '../../features/registry';
import { THEME_CHOICES, useThemeChoice, useThemeStore, watchSystemTheme } from '../../lib/theme';
import { SUPPORTED_LOCALES } from '../../lib/format';
import { FOCUS_RING } from '../../primitives/focus';
import * as RadixPopover from '@radix-ui/react-popover';
import { useFeedback } from '../feedback/FeedbackProvider';
import { OptimaAvatar } from '../optima/OptimaAvatar';
import { useOptima } from '../optima/OptimaProvider';
import { DensityControl } from '../../planning/density-control';
import { i18next } from '../../i18n';
import { logout, useAuthSession } from '../../auth/session';

const THEME_ICON = { light: Sun, dark: Moon, system: Monitor } as const;

/** The focus ring inside the rail, whose offset colour is the rail and not the page. */
const RAIL_FOCUS = 'focus-visible:outline-rail-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';

export type { AppChromeProps } from './types';

export function AppChrome({ children, currentPath, params, onNavigate, version, commit }: AppChromeProps): React.JSX.Element {
  const { t } = useTranslation();
  const themeChoice = useThemeChoice();
  const setThemeChoice = useThemeStore((s) => s.setChoice);
  const { actor, displayName, isAuthenticated } = useAuthSession();
  const feedback = useFeedback();
  const optima = useOptima();
  /** Who is signed in, as a person where the token says so and as a login otherwise. */
  const identity = isAuthenticated ? displayName ?? actor ?? undefined : undefined;
  const chapters = chapterNavigation();
  /** Below the `lg` token the rail is icons only — `./useWideRail.ts`, audit v4 G-15. */
  const wide = useWideRail();
  const activeChapter = chapters.find(
    (c) => currentPath === `/${c.slug}` || currentPath.startsWith(`/${c.slug}/`),
  );

  /** The rail's app groups (ADR 9023), and the entry the planner is on — the one the indicator marks. */
  // An entry whose route the platform denies this person is not drawn (ADR 9037).
  const withheld = useWithheldPaths();
  const rail = railLayout(chapters, undefined, withheld);
  const here = activeRailLink(rail, currentPath, params);
  const inFuture = here === undefined
    && rail.future.some((item) => isFutureHere(item, currentPath, activeChapter?.slug));
  // An app drawn inside Future expansions opens that group, not a dropdown of its own (`docs/adr/9082`).
  const [openGroups, toggleGroup] = useRailOpenGroups(here === undefined ? (inFuture ? FUTURE_GROUP : undefined) : railGroupOf(rail, here.app));
  /** A rail entry opens its first usable view, with the query the entry sets (Simulate's stage,
   *  Documentation's section) and otherwise the shell's own carry — `railNavigationParams`. */
  const openLink = (link: RailLink): void => {
    const to = firstUsable(link.entry).to;
    const next = railNavigationParams(rail, link, params ?? {});
    if (next === undefined) onNavigate(to);
    else onNavigate(to, next);
  };

  /** Every other move out of the menu (Cockpit, My work, the palette, the collapsed rail) leaves
   *  the run behind too: the page it opens starts from its own newest run (ADR 9039, 2026-10-22). */
  const menuNavigate = (to: string): void => {
    const next = withoutRunParams(params ?? {});
    if (next === undefined) onNavigate(to);
    else onNavigate(to, next);
  };

  const [paletteOpen, setPaletteOpen] = useState(false);
  /** The bar's slot a screen portals its own scope into (`./top-bar-slot.tsx`, brief part 1 §6). */
  const [topBarSlot, setTopBarSlot] = useState<HTMLElement | null>(null);

  /** The parameters the screen DECLARES it reads, as they stand in the address bar (issue 350,
   *  ADR 9054): the bar still carries every parameter across screens, the header names only
   *  those that condition this one. `./scope-chips.ts`. */
  const scopeChipList = useMemo(
    () => scopeChips(params ?? {}, declaredUrlParams(currentPath, resolveRoute(currentPath)?.route.route)),
    [params, currentPath],
  );

  /** The chips' VALUES in words (owner decision 2026-10-11): a department family or a season the
   *  platform publishes a label for, asked only for the codes a chip carries — none, no request. */
  const chipCodesIn = (vocabulary: string): readonly string[] => scopeChipList
    .filter(([key]) => scopeChipVocabulary(key) === vocabulary)
    .map(([, value]) => value);
  const familyLabels = useVocabularyLabels(VOCABULARY_DEPARTMENT_FAMILY, chipCodesIn(VOCABULARY_DEPARTMENT_FAMILY));
  const seasonLabels = useVocabularyLabels(VOCABULARY_SEASON, chipCodesIn(VOCABULARY_SEASON));
  /** A chip's value as the planner reads it: the closed set's word, the vocabulary's label, else the value as it is. */
  const chipValueWords = (key: string, raw: string): string => {
    const valueKey = scopeChipValueKey(key, raw);
    if (valueKey !== undefined) return t(valueKey);
    const vocabulary = scopeChipVocabulary(key);
    if (vocabulary === VOCABULARY_DEPARTMENT_FAMILY) return familyLabels.labelOf(raw);
    if (vocabulary === VOCABULARY_SEASON) return seasonLabels.labelOf(raw);
    return raw;
  };

  /** The entry the planner is on, for the breadcrumb. Longest match, so a screen mounted
   *  under another screen's path names itself rather than its parent. */
  const activeScreen = useMemo(
    () => (activeChapter === undefined ? [] : screensOf(activeChapter.entries))
      .filter((e) => currentPath === e.to || currentPath.startsWith(`${e.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0],
    [activeChapter, currentPath],
  );

  useEffect(() => watchSystemTheme(), []);
  /**
   * ⌘K on a Mac, Ctrl+K elsewhere. Bound on the window rather than on the rail, because
   * the shortcut has to work while focus is in the grid — which is where a planner
   * actually is when they want to go somewhere else. `preventDefault` because Ctrl+K is
   * the browser's own search-bar shortcut in more than one browser, and losing the
   * keystroke to the address bar would be worse than not binding it at all.
   */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== 'k') return;
      if (!(isAppleKeyboard ? event.metaKey : event.ctrlKey)) return;
      event.preventDefault();
      setPaletteOpen((wasOpen) => !wasOpen);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="flex min-h-dvh">
      {/* DEC-102: the signed-in person's claims, copied once per session (inert until pinned). */}
      <SessionClaimsSync />
      {/* Audit 2026-10-10, D: a line across the top while any read is in flight, on every page. */}
      <FetchProgressBar />
      {/* §12.8 — full keyboard operability. The skip link is the first focusable thing on
          the page; without it a keyboard user tabs the whole rail on every screen. */}
      <a
        href="#main"
        className={`text-body bg-surface-raised text-ink-primary border-hairline sr-only rounded-control border px-3 py-1 focus:not-sr-only focus:absolute focus:z-chrome focus:m-2 ${FOCUS_RING}`}
      >
        {t('app.skipToContent')}
      </a>

      <aside
        // `print:hidden`: a printed page (the monthly management pack, ADR 3002) is the content alone.
        className={`bg-rail-bg border-rail-hairline z-chrome sticky top-0 flex h-dvh shrink-0 flex-col overflow-y-auto border-e print:hidden ${wide ? 'w-sidebar' : 'w-sidebar-collapsed'}`}
      >
        {wide ? (<>
        <div className="flex shrink-0 items-center px-4 py-4">
          {/*
            §5.2 rule 1, site 1 of 4 — the logo, in the rail's own tone because brand red on
            the ink plane is about 2.5:1 and the mark has to be recognised, not deciphered
            (§3). The product label beside it (audit v5b R-07) says which Bata this is: the
            mark alone names the company, and a planner with the storefront open in the next
            tab has two of them.

            THE MARK IS THE WAY BACK. Until this, `/` was reachable only by typing it: the
            rail lists the six chapters and the overview belongs to none of them, so a
            planner who had drilled into a screen had no control that returned them. The
            logo is where every other product on the planet puts that control, and a planner
            will try it whether or not it works.

            A `<button>`, not an `<a href>`: this shell navigates through the History API and
            every other control in the rail is a button, so a lone anchor would be the one
            thing that full-page-reloads. Its accessible name comes from the mark's own
            "Bata" plus the destination, because "Bata" alone names the brand and not what
            pressing it does. `aria-current` marks it when the overview IS the page, the same
            as any chapter.
          */}
          <button
            type="button"
            onClick={() => menuNavigate(HOME_PATH)}
            aria-current={currentPath === HOME_PATH ? 'page' : undefined}
            className={`hover:bg-rail-hover -m-1 flex items-center gap-2 rounded-control p-1 ${RAIL_FOCUS}`}
          >
            <BataWordmark tone="rail" />
            {/* `aria-hidden`: the button's name is where it GOES, and "Bata Planning Cockpit"
                would name a product and a place as one thing. */}
            <span aria-hidden className="text-micro text-rail-fg-secondary uppercase">{t('app.productLabel')}</span>
            <span className="sr-only">{t('nav.home')}</span>
          </button>
        </div>

        {/* The way into the palette for a planner who has not learnt the shortcut, and the
            place the shortcut is TAUGHT — a keystroke nobody is told about is a keystroke
            nobody uses. */}
        <div className="shrink-0 px-4 pb-3">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className={`bg-rail-raised border-rail-hairline text-label text-rail-fg-secondary hover:border-rail-fg flex h-8 w-full items-center gap-2 rounded-control border px-2 text-start ${RAIL_FOCUS}`}
          >
            <Search size={14} aria-hidden className="shrink-0" />
            <span className="grow truncate" title={t('palette.open')}>{t('palette.open')}</span>
            <kbd className="border-rail-hairline text-micro shrink-0 rounded-chip border px-1">
              {t(isAppleKeyboard ? 'palette.shortcut' : 'palette.shortcutControl')}
            </kbd>
          </button>
        </div>

        {/* One entry per chapter, labelled with the short `navLabelKey` rather than the
            full breadcrumb `titleKey`. The active chapter's own screens are nested
            underneath it — permanently, which is the point of the rail: the planner reads
            where they are and what else that chapter holds without the list collapsing the
            moment they look at the content.

            THE LIST SCROLLS, THE FOOT STAYS. `min-h-0` lets the list give up height to the
            foot, and without its own `overflow-y-auto` it gave it up by PAINTING PAST its box:
            opening a chapter taller than the space left (in-season, whose markdown screen
            lists its views) drew the screens straight over the theme, identity and version, unreadable in both. The
            list is now its own scroll container, so the mark, the palette and the foot keep
            their place at any height and any number of opened rows. */}
        <nav aria-label={t('nav.label')} onKeyDown={moveRailFocus} className="flex min-h-0 grow flex-col overflow-y-auto overscroll-contain">
          {/*
            THE COCKPIT IS A PLACE, SO IT IS AN ENTRY — audit v5 N-06 / v5b R-02.

            On `/` nothing in the rail was marked: the overview belongs to no chapter, so the
            one brand red that says "you are here" had nowhere to go, and the way back was a
            logo 36px wide. The logo stays the way home (every product puts it there); this is
            the same destination stated as navigation, with the indicator where the planner is.
            Not a chapter and not from a manifest: `/` is the shell's own route (`HOME_PATH`).
          */}
          {/* "My work" sits under the Cockpit (bata-ui#166 W0-8, ADR 9022): the other shell
              place a person starts a day from — what is theirs to act on now. */}
          {SHELL_ENTRIES.map((entry) => {
            const current = currentPath === entry.path;
            const Icon = entry.icon;
            return (
              <div key={entry.path} className={`relative flex items-center ${current ? 'bg-rail-active' : 'hover:bg-rail-hover'}`}>
                {current && <span aria-hidden className="bg-brand-red absolute inset-y-1 start-0 w-[3px]" />}
                <button
                  type="button"
                  {...RAIL_ITEM}
                  onClick={() => menuNavigate(entry.path)}
                  aria-current={current ? 'page' : undefined}
                  className={[
                    // The app headings' geometry and ink (brief part 1 §3, `RailApps.tsx`), so
                    // the Cockpit, My work and the apps read as one column of 18px icons.
                    'text-body flex min-h-[36px] min-w-0 grow items-center gap-3 py-1 pe-4 ps-[20px] text-start',
                    current ? 'text-rail-fg font-bold' : 'text-rail-fg-entry hover:text-rail-fg',
                    RAIL_FOCUS,
                  ].join(' ')}
                >
                  <Icon size={18} aria-hidden className="shrink-0" />
                  <span className="grow truncate" title={t(entry.labelKey)}>{t(entry.labelKey)}</span>
                  {/* bata-ui#166 W3-4 (ADR 9101): alerts assigned to me, from /me/work-counts. */}
                  {entry.path === MY_WORK_PATH && <WorkBadge />}
                </button>
              </div>
            );
          })}
          {/*
            THE RAIL, BY APP — ADR 9023 (`./rail-layout.ts`, `./RailApps.tsx`).

            Pricing, Replenishment, Compacting and Management as dropdowns, the one holding
            the page open; then "Future expansions": every screen no app lists, named so the
            planner can see what is coming, and inert.
          */}
          <RailApps
            layout={rail}
            active={here}
            currentPath={currentPath}
            activeSlug={activeChapter?.slug}
            open={openGroups}
            onToggle={toggleGroup}
            onOpenLink={openLink}
            focus={RAIL_FOCUS}
          />
        </nav>

        {/* The rail's foot. Everything here is about the planner and the build rather than
            about the estate, which is why it is separated from the navigation by the whole
            remaining height rather than by a rule. */}
        <div className="border-rail-hairline flex shrink-0 flex-col gap-2 border-t px-4 py-3">
          {/* §9 — the theme toggle wins over the OS setting IN BOTH DIRECTIONS, which is
              why there are three choices and not two. */}
          {/*
            `min-w-0` here and on each button, or the control leaves the rail.

            A `fieldset` carries `min-inline-size: min-content` in the UA stylesheet, and a
            `grow` flex item's automatic minimum size is its own min-content. Between them
            the three labelled choices could not shrink, so the control was 231.7px wide
            inside a 215px gutter in English — flush to the rail's edge where the select
            above it stops 16px short — and 257.7px in Italian and 263.8px in Spanish,
            where "Come il sistema" and "Según el sistema" were cut off mid-word at the
            rail's hard edge with no ellipsis and a scrollbar under the whole rail. The
            `truncate` on each label was written for exactly this and could never fire.
          */}
          <fieldset className="border-rail-hairline flex min-w-0 items-stretch rounded-control border">
            <legend className="sr-only">{t('theme.label')}</legend>
            {/*
              ADR 9004 - the choice is WRITTEN, not only drawn.

              These were three mute icons, and a planner looking for the light theme had to
              guess which glyph meant it. The redesign labels them, and the label is also
              what makes the control findable at all: an icon-only toggle is discoverable
              by people who already know it is there.

              THREE choices, where the mockup draws two. §9 requires the toggle to beat the
              OS setting IN BOTH DIRECTIONS, which needs a third state for "follow the OS" -
              otherwise an explicit light choice is indistinguishable from no choice at all.
              The mockup has two because a design tool has no OS theme to follow.
            */}
            {THEME_CHOICES.map((choice) => {
              const Icon = THEME_ICON[choice];
              const selected = themeChoice === choice;
              return (
                <button
                  key={choice}
                  type="button"
                  onClick={() => setThemeChoice(choice)}
                  aria-pressed={selected}
                  className={[
                    // Icon above, word below, on equal thirds (designer audit B2). Side by
                    // side, three icon-and-word choices did not fit 215px of rail in any
                    // locale, and Italian and Spanish ellipsised to "Come il s…" and "Según
                    // el…". Stacked in `text-micro`, each label has its third of the rail to
                    // itself and wraps onto a second line when it needs one, so every
                    // locale reads its choice in full.
                    'text-micro inline-flex min-h-8 min-w-0 flex-1 flex-col items-center justify-center gap-[2px] px-1 py-1 text-center',
                    selected ? 'bg-rail-active text-rail-fg' : 'text-rail-fg-secondary hover:text-rail-fg',
                    RAIL_FOCUS,
                  ].join(' ')}
                >
                  <Icon size={13} aria-hidden className="shrink-0" />
                  <span className="max-w-full [overflow-wrap:anywhere]">{t(`theme.${choice}`)}</span>
                </button>
              );
            })}
          </fieldset>

          {/* Identity. The chrome mounts behind the login gate, so `isAuthenticated` is
              true in practice by the time this renders — checked rather than assumed, since
              a defensive render here is free and a route ordering change elsewhere is not
              this file's contract to rely on. */}
          {/*
            THE LANGUAGE LIVES BEHIND THE IDENTITY. It was a native select the full width of the
            rail, as prominent as the theme — and a language is chosen once, not per session
            (audit v4 G-19, audit-grafico B6). The planner's name now opens a small menu that
            holds it; sign-out stays one press away beside it, never inside a menu.

            Never lost: with no identity to hang it on, the same menu opens from a "Language"
            button, so there is no state of this chrome with no way to change language.
          */}
          <div className="flex items-center gap-1">
            {/*
              The Popover primitive, written out: it has no `onCloseAutoFocus`, and this menu needs it
              (bata-platform#686). «Report a problem» closes the menu to open a non-modal drawer, and
              the focus Radix gives the trigger back on close would land outside the drawer and close
              it. The content is the primitive's own look.
            */}
            <RadixPopover.Root>
              <RadixPopover.Trigger asChild>
                <button
                  type="button"
                  // The planner's NAME — data, not copy. `translate="no"` is HTML's own word for
                  // that, and the pseudo-locale test honours it: without it every page failed as
                  // "hard-coded" because "e2e-fixture" is plain ASCII inside a button.
                  translate={identity !== undefined ? 'no' : undefined}
                  className={`text-label text-rail-fg-secondary hover:text-rail-fg flex h-8 min-w-0 grow items-center gap-2 rounded-control px-1 text-start ${RAIL_FOCUS}`}
                >
                  {/* The initials square — audit v5b R-08. No role beside the name: a planner
                      holds several realm roles at once (up to six in the realm export) and
                      naming ONE of them would be this layer choosing which. */}
                  {identity !== undefined && <IdentityMark name={identity} />}
                  <span className="min-w-0 grow truncate" title={identity ?? t('locale.label')}>{identity ?? t('locale.label')}</span>
                  <ChevronDown size={13} aria-hidden className="shrink-0" />
                </button>
              </RadixPopover.Trigger>
              <RadixPopover.Portal>
                <RadixPopover.Content
                  align="start"
                  sideOffset={4}
                  {...(feedback === null ? {} : { onCloseAutoFocus: feedback.keepFocusInForm })}
                  className="bg-surface-raised border-popover-border shadow-popover z-popover max-h-[var(--radix-popover-content-available-height)] min-w-[14rem] overflow-y-auto rounded-control border p-3"
                >
              {/* Locale. G6 — adding a language is a catalogue plus a configuration row, so
                  this list is derived, never hand-written. */}
              <div className="flex flex-col gap-1">
                <label className="text-label text-ink-secondary" htmlFor="locale-select">{t('locale.label')}</label>
                <select
                  id="locale-select"
                  value={i18next.language}
                  onChange={(e) => { void i18next.changeLanguage(e.target.value); }}
                  className={`bg-surface-raised border-hairline text-body text-ink-primary h-8 rounded-control border px-1 ${FOCUS_RING}`}
                >
                  {SUPPORTED_LOCALES.map((locale) => (
                    <option key={locale} value={locale}>{t(`locale.${locale}`)}</option>
                  ))}
                </select>
              </div>
              {/* A preference, beside the other one — ADR 0088 as amended, audit v5b D-08. */}
              <div className="mt-3">
                <DensityControl />
              </div>
              {/* Only for a signed-in planner: there is no password to change otherwise. */}
              {isAuthenticated && (
                <div className="mt-3">
                  <AccountEntries onNavigate={menuNavigate} />
                </div>
              )}
                </RadixPopover.Content>
              </RadixPopover.Portal>
            </RadixPopover.Root>
            {isAuthenticated && (
              <button
                type="button"
                onClick={logout}
                aria-label={t('nav.logout')}
                className={`size-8 inline-flex shrink-0 items-center justify-center text-rail-fg-secondary hover:text-rail-fg ${RAIL_FOCUS}`}
              >
                <LogOut size={13} aria-hidden />
              </button>
            )}
          </div>

          {/* G7.5 — "the UI footer displays the version". On one line (audit v5b R-08):
              "Version 0.8.0 – build local" wrapped to two in mono. The long form stays the
              title, for whoever is reading it out to support. */}
          <span
            title={t('app.versionFooter', { version, commit })}
            className="text-micro text-rail-fg-secondary font-numeric truncate tabular-nums"
          >
            {t('app.versionShort', { version, commit })}
          </span>
        </div>
        </>) : (
          <CollapsedRail
            layout={rail}
            active={here}
            activeSlug={activeChapter?.slug}
            onOpenLink={openLink}
            currentPath={currentPath}
            homePath={HOME_PATH}
            onNavigate={menuNavigate}
            onOpenPalette={() => setPaletteOpen(true)}
            themeChoice={themeChoice}
            onThemeChoice={setThemeChoice}
            identity={identity}
            onLogout={logout}
            version={version}
            commit={commit}
            focus={RAIL_FOCUS}
          />
        )}
      </aside>

      <div className="flex min-w-0 grow flex-col">
        {/* The bar above the content. It states WHERE the planner is, which the rail no
            longer has to repeat in a breadcrumb of its own, and it is where the scope chips
            will mount once the cockpit names a run. §6 — 56px in-app, not the retail site's
            64px, "which wastes vertical space in a grid application". */}
        <header
          // `surface-page`, not `surface-raised`: a #ffffff bar over a #fdfcfb page read as a
          // colour error (audit v4 G-17). The hairline alone separates them.
          className="bg-surface-page border-hairline z-chrome flex shrink-0 items-center gap-2 border-b px-4 print:hidden"
          style={{ height: 'var(--bata-chrome-header-height)' }}
        >
          {/* The breadcrumb — chapter, then screen. It is built from the same nav model the
              rail reads, so it cannot disagree with it, and it is the ONLY place the
              current screen is named in words: the rail marks it, the title on the screen
              states it, and a third spelling would be a third thing to keep in step.
              The scope chips (`?runId=`, `?asOf=`) mount beside it once a screen names a
              run; until then this bar is deliberately quiet rather than filled. */}
          {currentPath === HOME_PATH && (
            // The overview belongs to no chapter, so the chapter/screen trail below has
            // nothing to say — and the bar said nothing at all (audit v4 G-06).
            <nav aria-label={t('nav.breadcrumb')} className="flex min-w-0 items-center gap-2">
              <span className="text-body text-ink-primary truncate font-semibold" title={t('home.title')}>{t('home.title')}</span>
            </nav>
          )}
          {activeChapter && (
            <nav aria-label={t('nav.breadcrumb')} className="flex min-w-0 items-center gap-2">
              {/*
                `truncate`, not `shrink-0`. The bar's other two occupants — the chip strip
                and the feed pill — are laid out first, so on a narrow window this nav is
                given less room than the breadcrumb needs. A `shrink-0` label does not stay
                inside a box that small: it PAINTS OUTSIDE it, over the chips and under the
                pill, which is where "In-season" was found overhanging its nav by 25.6px in
                Spanish at 768px and where the screen name disappeared behind the pill
                entirely at 200% zoom. Shrinking with an ellipsis is the same degradation
                the screen name below already has, and it keeps the breadcrumb inside the
                bar at every width.
              */}
              <span className="text-micro text-ink-secondary truncate" title={t(activeChapter.navLabelKey)}>
                {t(activeChapter.navLabelKey)}
              </span>
              {activeScreen && (
                <>
                  <span aria-hidden className="text-ink-muted shrink-0">/</span>
                  <span className="text-body text-ink-primary truncate font-semibold" title={t(activeScreen.labelKey)}>
                    {t(activeScreen.labelKey)}
                  </span>
                </>
              )}
            </nav>
          )}

          {/*
            The perimeter, as chips — ADR 9004.

            G2.2 makes the URL the state and G16 makes an unstated perimeter a read over the
            whole estate. The screen names its own scope in words; this says the same thing
            in the one place that is on every screen, so a planner reading a figure can see
            what it is a figure OF without scrolling to find out.

            It names the parameter in words and shows its value, and it interprets nothing: the
            words come from `./scope-chips.ts` (A6, owner decision 2026-10-10), the chrome has no
            business knowing what `runId` means, and a parameter with no label draws its value
            alone. The label and the whole value travel in the chip's title, for whoever needs
            to read a value the chip cut.
          */}
          {/*
            The screen's own scope, when it states one here — the Cockpit's market and run, with
            the run picker in the run chip's popover (brief part 1 §6, designer item 2.7). The
            chrome offers the place and interprets nothing; `empty:hidden` keeps the bar as quiet
            as before on every screen that puts nothing in it.
          */}
          <div ref={setTopBarSlot} data-top-bar-slot="" className="flex min-w-0 items-center gap-2 empty:hidden" />
          {scopeChipList.length > 0 && (
            <ul aria-label={t('nav.scope')} className="flex min-w-0 shrink items-center gap-1 overflow-x-auto">
              {scopeChipList.map(([key, rawValue]) => {
                // The parameter in words (A6): «Negozio: 5262». One with no label yet draws its value.
                // A value from a closed set or a published vocabulary is in words too, while an
                // identifier (a store, an article, a market, a date) stays as it is (2026-10-11).
                const labelKey = scopeChipLabelKey(key);
                const label = labelKey === undefined ? undefined : t(labelKey);
                // An identifier (a scenario, a forecast, a model version) is not printed: the chip
                // names the parameter and the id sits behind the small «id» (audit 2, A3/A4).
                if (label !== undefined && key === 'f.reason') return <li key={key}><ReasonChip label={label} code={rawValue} /></li>;
                if (label !== undefined && scopeChipValueIsOpaqueId(key)) {
                  return (
                    <li key={key}>
                      <span title={label} className="bg-pill text-caption text-ink-primary rounded-chip inline-flex max-w-[16rem] items-baseline gap-1 px-2 py-1">
                        <span className="truncate">{label}</span>
                        <IdTag id={rawValue} kind={label} />
                      </span>
                    </li>
                  );
                }
                const value = chipValueWords(key, rawValue);
                return (
                  <li key={key}>
                    <span
                      // The key AND the value (usability audit D9): the chip cuts a long value at
                      // 16rem, and a title holding only the key left the cut value unreadable.
                      title={label === undefined ? t('nav.scopeChipTitle', { key, value }) : t('nav.scopeChipLabelled', { label, value })}
                      // `max-w-[16rem]`, not `max-w-64`: the spacing scale is the token
                      // scale (0/4/8/12/16/24/32/48/64px), so `64` is 64 PIXELS and the
                      // Tailwind default that would have meant 16rem does not exist here —
                      // the class emitted nothing and an option key rendered 506px wide,
                      // which is what made the chip strip need 900px of its own scrolling.
                      // `text-ink-primary`, not secondary: `color.pill` is the darkest light
                      // surface in the system and secondary ink measures 4.31:1 on it, under
                      // WCAG AA's 4.5:1 — and the scope is not decoration. Every figure on the
                      // screen is conditioned by these values.
                      className="bg-pill text-caption text-ink-primary rounded-chip inline-block max-w-[16rem] truncate px-2 py-1"
                    >
                      {label === undefined ? value : t('nav.scopeChipLabelled', { label, value })}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          {/*
            OPTIMA (bata-platform epic 719, ADR 9080). Drawn only where the platform says the
            assistant is on; the panel it opens is mounted by `../optima/OptimaProvider.tsx`, and
            Alt+O does the same from anywhere (Ctrl/⌘ K is the palette's). `ms-auto` puts it at the
            inline end of the bar, away from the breadcrumb and the chips. Its name is the visible
            word; the keys are in the title and in `aria-keyshortcuts`, not in the name. The box beside
            it is OPTIMA's avatar (ADR 9077): closed with the panel shut, open and empty while the
            shoe is out in the panel.
          */}
          {optima?.enabled === true && (
            <button
              ref={optima.triggerRef}
              type="button"
              onClick={optima.toggle}
              aria-expanded={optima.open}
              aria-keyshortcuts="Alt+O"
              title={t('optima.open.title', { shortcut: t(isAppleKeyboard ? 'optima.shortcut' : 'optima.shortcutAlt') })}
              className={`bg-surface-raised border-hairline text-label text-ink-primary hover:bg-surface-sunken rounded-control ms-auto flex h-8 shrink-0 items-center gap-2 border px-2 ${FOCUS_RING}`}
            >
              {/* The closed box with the Bata logo; the open, empty one while the shoe is in the panel (ADR 9077).
                  It has no loop in any state, and the word beside it is its name. */}
              <OptimaAvatar state={optima.open && optima.avatarState !== 'pronto' ? 'vuota' : 'pronto'} size={20} decorative />
              <span>{t('optima.name')}</span>
              <kbd aria-hidden className="border-hairline text-micro text-ink-secondary rounded-chip border px-1">
                {t(isAppleKeyboard ? 'optima.shortcut' : 'optima.shortcutAlt')}
              </kbd>
            </button>
          )}
        </header>

        <main id="main" aria-label={t('app.mainLandmark')} className="grow">
          <TopBarSlotProvider slot={topBarSlot}>{children}</TopBarSlotProvider>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} onNavigate={menuNavigate} withheld={withheld} />
    </div>
  );
}
